import { Injectable } from '@angular/core';
import { CollectionReference, DocumentReference, Firestore, collection, doc, getDoc, getDocs, runTransaction } from '@angular/fire/firestore';
import { deleteDoc } from 'firebase/firestore';

import * as models from '../models';
import { HelperService } from './helper.service';
import { FileService } from './file.service';

// CRUD for floor-plan documents stored at companies/{companyId}/floor-plans/{id}.
// Mirrors AttachmentService: the picked image goes to Firebase Storage via
// FileService and the Firestore doc keeps only the storage fullPath + metadata.
@Injectable()
export class FloorPlanService {

    companiesRef: CollectionReference;
    slug: string = 'floor-plans';

    // storage path template understood by FileService ({companyId} is substituted)
    private storagePath(id: string): string {
        // Versioned objects prevent a failed/concurrent metadata update from
        // overwriting the image referenced by the currently saved plan.
        return `floorPlans/{companyId}/${id}/${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    constructor(
        private firestore: Firestore,
        private helperService: HelperService,
        private fileService: FileService,
    ) {
        this.companiesRef = collection(this.firestore, 'companies');
    }

    private getCollectionRef() {
        let companyId = this.helperService.currentCompanyId;
        let companyRef = doc(this.companiesRef, companyId);
        return collection(companyRef, this.slug);
    }

    async get(id: string): Promise<models.FloorPlan> {
        let response = await getDoc(doc(this.getCollectionRef(), id));
        if (!response.exists()) {
            return null;
        }
        let floorPlan: models.FloorPlan = response.data();
        floorPlan.id = response.id;
        return this.normalize(floorPlan);
    }

    async getAll(): Promise<Array<models.FloorPlan>> {
        let response = await getDocs(this.getCollectionRef());
        let retVal: Array<models.FloorPlan> = [];
        response.docs.forEach(d => {
            let floorPlan: models.FloorPlan = d.data();
            floorPlan.id = d.id;
            retVal.push(this.normalize(floorPlan));
        });
        return retVal;
    }

    // Returns a browser-usable download URL for the plan's image.
    getImageUrl(floorPlan: models.FloorPlan): Promise<string> {
        if (floorPlan == null || floorPlan.fullPath == null) {
            return Promise.resolve(null);
        }
        // fullPath is already company-qualified; pass 'none' so FileService does
        // no token substitution.
        return this.fileService.get(floorPlan.fullPath, 'none').toPromise();
    }

    // Saves library metadata immediately. Workspace drawing edits are saved
    // atomically with zones through ZoneService.saveWorkspace().
    async save(floorPlan: models.FloorPlan): Promise<string> {
        let colRef = this.getCollectionRef();

        let docRef: DocumentReference;
        if (floorPlan.id) {
            docRef = doc(colRef, floorPlan.id);
        } else {
            docRef = doc(colRef);
        }

        let uploadedPath: string;
        let previousPath = floorPlan.fullPath;
        if (floorPlan.file != null) {
            let response = await this.fileService.upload(this.storagePath(docRef.id), floorPlan.file);
            uploadedPath = response.metadata.fullPath;
        }

        let expectedRevision = this.validRevision(floorPlan.revision);
        try {
            let nextRevision = await runTransaction(this.firestore, async transaction => {
                let snapshot = await transaction.get(docRef);
                let actualRevision = snapshot.exists()
                    ? this.validRevision((snapshot.data() as models.FloorPlan).revision)
                    : 0;
                if (snapshot.exists() && actualRevision !== expectedRevision) {
                    throw new Error('FLOOR_PLAN_REVISION_CONFLICT');
                }
                let { id, file, ...metadata } = floorPlan;
                if (uploadedPath) metadata.fullPath = uploadedPath;
                let revision = actualRevision + 1;
                transaction.set(docRef, this.firestoreValue({ ...metadata, revision }));
                return revision;
            });
            floorPlan.id = docRef.id;
            floorPlan.revision = nextRevision;
            if (uploadedPath) floorPlan.fullPath = uploadedPath;
            delete floorPlan.file;
            if (uploadedPath && previousPath && previousPath !== uploadedPath) {
                try { await this.fileService.delete(previousPath, 'none').toPromise(); } catch (_) { }
            }
        } catch (e) {
            if (uploadedPath) {
                try { await this.fileService.delete(uploadedPath, 'none').toPromise(); } catch (_) { }
            }
            throw e;
        }

        return docRef.id;
    }

    async delete(floorPlan: models.FloorPlan): Promise<void> {
        let colRef = this.getCollectionRef();

        if (floorPlan.fullPath != null) {
            try {
                await this.fileService.delete(floorPlan.fullPath, 'none').toPromise();
            } catch (e) {
                // tolerate a missing/already-removed storage object
            }
        }
        await deleteDoc(doc(colRef, floorPlan.id));
    }

    async deleteStorageOnly(floorPlan: models.FloorPlan): Promise<void> {
        if (!floorPlan?.fullPath) return;
        try {
            await this.fileService.delete(floorPlan.fullPath, 'none').toPromise();
        } catch (_) {
            // Firestore is authoritative; tolerate already-missing storage files.
        }
    }

    private normalize(floorPlan: models.FloorPlan): models.FloorPlan {
        floorPlan.canvasWidth = floorPlan.canvasWidth || floorPlan.imageWidth || 1000;
        floorPlan.canvasHeight = floorPlan.canvasHeight || floorPlan.imageHeight || 1000;
        floorPlan.backgroundFit = floorPlan.backgroundFit || 'fit';
        floorPlan.showGrid = floorPlan.showGrid !== false;
        floorPlan.elements = floorPlan.elements || [];
        floorPlan.revision = this.validRevision(floorPlan.revision);
        return floorPlan;
    }

    private validRevision(value: number): number {
        return Number.isFinite(value) && value >= 0 ? value : 0;
    }

    private firestoreValue<T>(value: T): T {
        return JSON.parse(JSON.stringify(value));
    }
}
